import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"

export type CapacityBarSize = "sm" | "md" | "lg"
export type CapacityBarSegmentVariant = "current" | "secondary" | "available"

export type CapacityBarSegment = {
  variant: CapacityBarSegmentVariant
  percent: number
}

const capacityBarChartVariants = cva("h-capacity-bar__chart", {
  variants: {
    size: {
      sm: "h-capacity-bar__chart--sm",
      md: "h-capacity-bar__chart--md",
      lg: "h-capacity-bar__chart--lg",
    },
    primaryOnly: {
      true: "h-capacity-bar__chart--primary-only",
    },
  },
  defaultVariants: {
    size: "sm",
  },
})

type CapacityBarChartVariants = VariantProps<typeof capacityBarChartVariants>

type CapacityBarProps = CapacityBarChartVariants & {
  children?: React.ReactNode
  className?: string
  chartClassName?: string
}

const capacityBarSegmentVariants = cva("h-capacity-bar__segment", {
  variants: {
    variant: {
      current: "h-capacity-bar__segment--current",
      secondary: "h-capacity-bar__segment--secondary",
      available: "h-capacity-bar__segment--available",
    },
  },
})

export function CapacityBarSegment({
  variant,
  percent,
}: {
  variant: CapacityBarSegmentVariant
  percent: number
}) {
  const clamped = Math.min(100, Math.max(0, percent))
  if (clamped <= 0) return null

  return (
    <div
      className={capacityBarSegmentVariants({ variant })}
      style={{ width: `${clamped}%` }}
    />
  )
}

function CapacityBarChart({
  size,
  primaryOnly,
  children,
  className,
}: CapacityBarChartVariants & {
  children?: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(capacityBarChartVariants({ size, primaryOnly }), className)}
    >
      {children}
    </div>
  )
}

export function CapacityBar({
  size = "sm",
  primaryOnly,
  children,
  className,
  chartClassName,
}: CapacityBarProps) {
  return (
    <div className={cn("h-capacity-bar", className)}>
      <CapacityBarChart
        size={size}
        primaryOnly={primaryOnly}
        className={chartClassName}
      >
        {children}
      </CapacityBarChart>
    </div>
  )
}

export type CapacityBarLegendItem = {
  variant: CapacityBarSegmentVariant
  label: string
  value: number
}

type CapacityBarLegendProps = {
  items: CapacityBarLegendItem[]
  className?: string
}

export function CapacityBarLegend({
  items,
  className,
}: CapacityBarLegendProps) {
  return (
    <div className={cn("h-capacity-bar__legend", className)}>
      {items.map((item) => (
        <div
          key={`${item.variant}-${item.label}`}
          className="h-capacity-bar__legend-item"
        >
          <div className="d-flex align-items-center gap-1">
            <div
              className={`h-capacity-bar__legend-color h-capacity-bar__legend-color--${item.variant}`}
            />
            <div className="text-2xs">{item.label}</div>
          </div>
          <div className="text-md-medium">{item.value}</div>
        </div>
      ))}
    </div>
  )
}
